import RiVideoLine from '@ben/icons/video-line';
import { Button, Popover, TextInput } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { RichTextEditor, useRichTextEditorContext } from '@mantine/tiptap';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import RIcon from '../remix-icon/remix-icon';

const ALLOWED_URL_PROTOCOLS = new Set(['http:', 'https:']);

const isValidUrl = (urlString: string): boolean => {
  try {
    const url = new URL(urlString);
    return ALLOWED_URL_PROTOCOLS.has(url.protocol);
  } catch {
    return false;
  }
};

const IframeEmbedControl = () => {
  const { editor } = useRichTextEditorContext();
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  const validateAndGetError = (urlValue: string): string => {
    if (!urlValue.trim()) {
      return '';
    }
    if (!isValidUrl(urlValue.trim())) {
      return t(
        'iframeEmbed.invalidUrl',
        'Please enter a valid URL (http:// or https://)'
      );
    }
    return '';
  };

  const handleInsert = () => {
    if (!url.trim() || !editor) return;

    const validationError = validateAndGetError(url);
    if (validationError) {
      setError(validationError);
      return;
    }

    editor
      .chain()
      .focus()
      .insertContent({
        type: 'iframe',
        attrs: { src: url.trim() },
      })
      .run();
    setUrl('');
    setError('');
    close();
  };

  const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newUrl = event.currentTarget.value;
    setUrl(newUrl);
    if (error) {
      setError(validateAndGetError(newUrl));
    }
  };

  const handleUrlBlur = () => {
    if (url.trim()) {
      setError(validateAndGetError(url));
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleInsert();
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setUrl('');
      setError('');
      close();
    }
  };

  const isInsertDisabled = !url.trim() || !!error;

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      trapFocus
      position="bottom"
      withArrow
    >
      <Popover.Target>
        <RichTextEditor.Control
          onClick={toggle}
          aria-label={t('iframeEmbed.embedVideo', 'Embed video')}
          title={t('iframeEmbed.embedVideo', 'Embed video')}
          data-testid="iframe-embed-button"
        >
          <RIcon icon={RiVideoLine} size="sm" />
        </RichTextEditor.Control>
      </Popover.Target>
      <Popover.Dropdown>
        <PopoverContent>
          <TextInput
            placeholder={t('iframeEmbed.enterUrl', 'Enter video URL')}
            value={url}
            onChange={handleUrlChange}
            onBlur={handleUrlBlur}
            onKeyDown={handleKeyDown}
            aria-label={t('iframeEmbed.videoUrl', 'Video URL')}
            error={error}
            size="sm"
          />
          <Button size="xs" onClick={handleInsert} disabled={isInsertDisabled}>
            {t('iframeEmbed.insert', 'Insert')}
          </Button>
        </PopoverContent>
      </Popover.Dropdown>
    </Popover>
  );
};

export default IframeEmbedControl;

const PopoverContent = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    min-width: 280px;
  `}
`;
